import { useCallback, useContext, useEffect, useState } from "react";

import { Cartesian } from "@examspark/types";

import {
  CartesianGraphContext,
  applyOffsetToCoordinate,
  getImageCoordsFromCartesianCoords,
  getRoundedCartesianCoordinateLegacy,
} from "./utils";

const defaultDragOffset: Cartesian.Coordinate = { x: 0, y: 0 };
let currentCoord: Cartesian.Coordinate | undefined;
interface DragParams {
  onDragEnd?: (e: any) => void;
  onDrag?: (e: any) => void;
  snapToGrid?: boolean /** @todo snapToGrid not implemented */;
  restrictDragAxis?: "x" | "y";
  bounds?: { x?: [number?, number?]; y?: [number?, number?] };
}

export const useCartesianDrag = (
  svg: SVGSVGElement | null,
  coord: Cartesian.Coordinate,
  params?: DragParams,
) => {
  const graph = useContext(CartesianGraphContext);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOrigin, setDragOrigin] = useState<Cartesian.ImageCoordinate>();
  const [dragOffset, setDragOffset] =
    useState<Cartesian.ImageCoordinate>(defaultDragOffset);

  const startDrag = useCallback(
    (e: any) => {
      setIsDragging(true);
      setDragOrigin({
        x: e.clientX,
        y: e.clientY,
      });
    },
    [isDragging],
  );

  const handleMouseMove = useCallback(
    (e: any) => {
      if (isDragging && dragOrigin) {
        setDragOffset({
          x: -(dragOrigin.x - e.clientX),
          y: -(dragOrigin.y - e.clientY),
        });
      }
    },
    [isDragging],
  );

  useEffect(() => {
    if (isDragging) {
      params?.onDrag?.(getCoordinates());
    }
  }, [dragOffset.x, dragOffset.y]);

  const handleMouseUp = useCallback(
    (e: any) => {
      params?.onDragEnd?.(getCoordinates());
      stopDrag(e);
    },
    [dragOffset, graph],
  );

  const stopDrag = (e: any) => {
    setIsDragging(false);
    setDragOffset(defaultDragOffset);
  };

  useEffect(() => {
    if (isDragging && svg) {
      // console.log("add events", svg, isDragging);
      svg?.addEventListener("mouseup", handleMouseUp);
      svg?.addEventListener("mousemove", handleMouseMove);
    }
    return () => {
      // console.log("remove events", svg, isDragging);
      svg?.removeEventListener?.("mouseup", handleMouseUp);
      svg?.removeEventListener?.("mousemove", handleMouseMove);
    };
  }, [isDragging, svg, dragOffset]);

  const getCoordinates = (): Cartesian.Coordinate => {
    if (!isDragging || !graph?.graph) return coord;

    const imgCoords = getImageCoordsFromCartesianCoords(graph.graph, coord);
    const offset = applyOffsetToCoordinate(imgCoords, dragOffset);
    // console.log("get coordinates", coord, dragOffset, imgCoords);
    // console.log("drag coord!", imgCoords, roundedCartesian);

    const roundedCartesian = graph.snapFn
      ? graph.snapFn(graph.graph, offset)
      : getRoundedCartesianCoordinateLegacy(graph.graph, offset);

    if (params?.bounds?.x) {
      console.log(params.bounds.x, currentCoord, roundedCartesian.x);
      if (
        (params.bounds.x[0] && roundedCartesian.x <= params.bounds.x[0]) ||
        (params.bounds.x[1] && roundedCartesian.x >= params.bounds.x[1])
      ) {
        if (currentCoord?.x) {
          roundedCartesian.x = currentCoord.x;
        }
      }
    }

    if (params?.restrictDragAxis) {
      switch (params.restrictDragAxis) {
        case "x":
          roundedCartesian.y = coord.y;
          break;
        case "y":
          roundedCartesian.x = coord.x;
          break;
      }
    }

    currentCoord = roundedCartesian;
    return roundedCartesian;
  };

  return {
    coord: getCoordinates(),
    dragOffset,
    isDragging,
    startDrag,
    stopDrag,
  };
};
